import { throttle } from 'lodash'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { SignupError } from '@pages/Signup/Pages/PersonalDetails/PersonalDetailsStep4/SignupErrorPage'

import { useEntitySettings } from '@context/EntityContext'
import { useSignup } from '@context/SignupContext'
import { useSnackbar } from '@context/SnackbarContext'

import { ResponseError, instanceOfAxiosError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { PhoneVerificationWorkflowEnum } from '@model/CreatePhoneNumberVerification'
import { LeadDto, isLeadSourceMobileRegistration } from '@model/LeadDto'
import { NameDto } from '@model/NameDto'
import { TickmillProductType } from '@model/TickmillProductType'

import { useLinksConfig } from '@hooks/useLinksConfig'
import { useLocallyPersistedState } from '@hooks/useStorage'

interface UseLeadOTPVerificationOptions {
  leadId: string | null
  email: string
  token: string | null
}

const isVerificationCodeError = (verificationCodeError?: string): boolean => {
  return (
    verificationCodeError === 'phone_verification_max_attempts_reached' ||
    verificationCodeError === 'max_new_verification_code_requests_reached'
  )
}

export function useLeadOTPVerification({ leadId, email, token }: UseLeadOTPVerificationOptions) {
  const { signupData, setLead } = useSignup()
  const apiClient = useApiClient(ClientApiClient)
  const navigate = useNavigate()
  const { addSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [, setEntity] = useEntitySettings()
  const { links } = useLinksConfig()
  const [, persistLead] = useLocallyPersistedState<LeadDto | undefined>('lead', undefined)

  const [resendLoading, setResendLoading] = useState(false)
  const [continueLoading, setContinueLoading] = useState(false)
  const verifiedLeadRef = useRef<LeadDto | null>(null)
  const leadEmail = useRef(email)

  const resolvedLeadId = useMemo(
    () => leadId ?? signupData.lead?.id ?? signupData.leadResponse?.leadId ?? null,
    [leadId, signupData.lead?.id, signupData.leadResponse?.leadId]
  )

  const resolvedEmail = useMemo(
    () => signupData.values?.email?.trim() || signupData.lead?.email || email,
    [email, signupData.lead?.email, signupData.values?.email]
  )

  const handleStocksRedirect = useCallback(
    (lead: LeadDto): boolean => {
      const hasOnlyStocks =
        lead.products.length > 0 &&
        lead.products.every((p: NameDto) => p.id === TickmillProductType.STOCKS)
      if (hasOnlyStocks) {
        window.location.replace(links.IBKR)
        return true
      }
      return false
    },
    [links.IBKR]
  )

  const persistAndStoreLead = useCallback(
    async (leadDto: LeadDto) => {
      leadEmail.current = leadDto.email
      verifiedLeadRef.current = leadDto
      setEntity({ entity: leadDto.tickmillCompany.id })
      persistLead(leadDto)
      setLead(leadDto)
    },
    [persistLead, setEntity, setLead]
  )

  const navigateToInfo = useCallback(
    async (leadDto: LeadDto) => {
      if (handleStocksRedirect(leadDto)) {
        return
      }

      const navToken = leadDto.token ?? token

      setLead(leadDto)

      if (navToken) {
        navigate(`/sign-up/personal-details/info/?hash=${encodeURIComponent(navToken)}`, {
          state: { leadId: leadDto.id },
          replace: true,
        })
      } else {
        navigate('/sign-up/personal-details/info/', {
          state: { leadId: leadDto.id },
          replace: true,
        })
      }
    },
    [handleStocksRedirect, navigate, setLead, token]
  )

  const navigateToPhoneVerification = useCallback(
    async (leadDto: LeadDto) => {
      const navToken = leadDto.token ?? token

      const createdPhoneVerification = await apiClient.createPhoneNumberVerification(leadDto.id, {
        phoneNumber: leadDto.phoneNumber,
        leadId: leadDto.id,
        countryCode: leadDto.countryCode,
        countryId: leadDto.country.id,
        workflowId: PhoneVerificationWorkflowEnum.WorkflowSMS,
      })

      setLead(leadDto)

      const searchParams = new URLSearchParams()

      if (navToken) {
        searchParams.set('token', navToken)
      }

      searchParams.set('phoneVerificationId', createdPhoneVerification.phoneVerificationId)

      navigate(
        `/sign-up/personal-details/contact/verify-phone/${leadDto.id}?token=${navToken}&phoneVerificationId=${createdPhoneVerification.phoneVerificationId}`
      )
    },
    [apiClient, navigate, setLead, token]
  )

  const handlePostVerificationFlow = useCallback(
    async (leadDto: LeadDto) => {
      await persistAndStoreLead(leadDto)

      if (isLeadSourceMobileRegistration(leadDto.leadSource)) {
        return
      }

      if (leadDto.phoneVerificationRequired) {
        await navigateToPhoneVerification(leadDto)
        return
      }

      await navigateToInfo(leadDto)
    },
    [navigateToInfo, navigateToPhoneVerification, persistAndStoreLead]
  )

  const handleVerificationFlowError = useCallback(
    (err: unknown, fallbackLeadId?: string | null) => {
      const errorResponse = (err as ResponseError).response?.response?.data

      if (!errorResponse?.code) {
        throw err
      }

      if (isVerificationCodeError(errorResponse.code)) {
        navigate('/sign-up/personal-details/info/', {
          state: {
            leadId: fallbackLeadId ?? signupData.lead?.id ?? signupData.leadResponse?.leadId,
          },
          replace: true,
        })
        return
      }

      if (errorResponse.code === SignupError.BackToLogin) {
        navigate('/login', { replace: true })
        return
      }

      const errorSearchParams = new URLSearchParams()
      errorSearchParams.set('error', errorResponse.code)

      const resolvedErrorLeadId =
        fallbackLeadId ?? signupData.lead?.id ?? signupData.leadResponse?.leadId ?? ''

      if (resolvedErrorLeadId) {
        errorSearchParams.set('leadId', resolvedErrorLeadId)
      }

      const resolvedErrorEmail = leadEmail.current || resolvedEmail

      if (resolvedErrorEmail) {
        errorSearchParams.set('leadEmail', resolvedErrorEmail)
      }

      navigate(`../error?${errorSearchParams.toString()}`, { replace: true })
    },
    [navigate, resolvedEmail, signupData.lead?.id, signupData.leadResponse?.leadId]
  )

  const handleResend = useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()

      try {
        setResendLoading(true)

        if (resolvedLeadId) {
          await apiClient.resendCreateLeadEmail(resolvedLeadId)
        } else if (resolvedEmail) {
          try {
            await apiClient.resendCreateLeadEmailByEmail(resolvedEmail)
          } catch (e: unknown) {
            if (instanceOfAxiosError((e as ResponseError)?.response)) {
              const errorResponse = (e as ResponseError).response.response?.data
              if (errorResponse?.code === SignupError.LeadVerificationErrorException) {
                // Ignore: lead is already verified
              } else if (errorResponse?.code) {
                throw e
              }
            }
          }
        } else {
          throw new Error('missing_email')
        }

        addSnackbar.success({ message: t('Email was re-sent') })
      } finally {
        setResendLoading(false)
      }
    },
    [addSnackbar, apiClient, resolvedEmail, resolvedLeadId, t]
  )

  const throttledHandleResend = useMemo(
    () => throttle(handleResend, 1000, { leading: true, trailing: false }),
    [handleResend]
  )

  const handleVerifyCode = useCallback(
    async (code: string) => {
      try {
        if (!resolvedLeadId && !resolvedEmail) {
          navigate('/sign-up/personal-details/basic/', { replace: true })
          return
        }

        const leadDto = await apiClient.verifyLeadUsingCode({
          code,
          ...(resolvedLeadId ? { leadId: resolvedLeadId } : { email: resolvedEmail }),
          verifyNonPrimarySourceLead: false,
        })

        if (handleStocksRedirect(leadDto)) {
          return
        }
        await persistAndStoreLead(leadDto)

        addSnackbar.success({ message: t('Sign up.Your email has been verified!') })
      } catch (e: unknown) {
        if (instanceOfAxiosError((e as ResponseError)?.response)) {
          const errorResponse = (e as ResponseError).response.response?.data

          if (errorResponse?.code) {
            throw new Error(errorResponse.code)
          }
        }

        throw e
      }
    },
    [resolvedLeadId, resolvedEmail, navigate, apiClient, persistAndStoreLead, addSnackbar, t]
  )

  const resolveLeadDto = useCallback(async (): Promise<LeadDto | null> => {
    if (verifiedLeadRef.current) {
      return verifiedLeadRef.current
    }

    if (signupData.lead) {
      return signupData.lead
    }

    return null
  }, [signupData.lead])

  const handleContinue = useCallback(async () => {
    try {
      setContinueLoading(true)

      const leadDto = await resolveLeadDto()

      if (!leadDto) {
        return
      }

      try {
        await handlePostVerificationFlow(leadDto)
      } catch (e: unknown) {
        if (instanceOfAxiosError((e as ResponseError)?.response)) {
          const errorResponse = (e as ResponseError).response.response?.data

          if (errorResponse?.code === SignupError.LeadVerificationErrorException) {
            setLead(leadDto)

            if (handleStocksRedirect(leadDto)) {
              return
            }

            if (
              !leadDto.phoneVerificationRequired &&
              !isLeadSourceMobileRegistration(leadDto.leadSource)
            ) {
              await navigateToInfo(leadDto)
              return
            }

            if (isLeadSourceMobileRegistration(leadDto.leadSource)) {
              leadEmail.current = leadDto.email
              return
            }

            if (leadDto.phoneVerificationRequired) {
              await navigateToPhoneVerification(leadDto)
              return
            }
          } else if (errorResponse?.code) {
            throw e
          }
        } else {
          throw e
        }
      }
    } catch (err: unknown) {
      handleVerificationFlowError(err, resolvedLeadId)
    } finally {
      setContinueLoading(false)
    }
  }, [
    handlePostVerificationFlow,
    handleStocksRedirect,
    handleVerificationFlowError,
    navigateToInfo,
    navigateToPhoneVerification,
    resolveLeadDto,
    resolvedLeadId,
    setLead,
  ])

  return {
    resolvedEmail,
    resendLoading,
    continueLoading,
    handleResend: throttledHandleResend,
    handleVerifyCode,
    handleContinue,
  }
}
